import { isWorkflowFormLookupBinding, type WorkflowFormLookupBinding } from '@app/shared/workflow-commands/workflow-form-lookup-contract';

export interface WorkflowLookupSourceContext {
  workflowCode: string;
  subjectTypeCode: string | null;
  scopeTypeCode: string;
  scopeKey: string | null;
}

/** authorityKey is client-only: it fences the actor/session and is never sent as authority. */
export interface WorkflowLookupSourceOwner {
  draftId: number;
  revision: number;
  etag: string;
  context: WorkflowLookupSourceContext;
  authorityKey: string;
}

export interface WorkflowLookupSource {
  binding: WorkflowFormLookupBinding;
  label: string;
  description: string | null;
  disposition: 'Resolved' | 'Missing' | 'Ambiguous' | 'Exempt';
  reasonCode: string | null;
  choices: { value: string; label: string; isEnabled: boolean }[];
  configurationVersionId?: number | null;
  versionCode?: string | null;
}

interface WorkflowLookupSourceReadOwner {
  draftId: number;
  revision: number;
  etag: string;
  context: WorkflowLookupSourceContext;
  asOfUtc: string;
}
export interface WorkflowLookupSourcePage extends WorkflowLookupSourceReadOwner {
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  items: WorkflowLookupSource[];
}
export interface WorkflowLookupSourceRead extends WorkflowLookupSourceReadOwner { source: WorkflowLookupSource; }
export interface WorkflowLookupSourceSelection { source: WorkflowLookupSource; owner: WorkflowLookupSourceOwner; context: string; }
export interface WorkflowLookupSourceDependency { id: string; label: string; }
export interface WorkflowLookupSourceQuery { search: string; pageNumber: number; pageSize: number; }

/** Authoring metadata follows the reader's case-insensitive source identity. Answers stay ordinal. */
export function workflowLookupSourceBindingKey(binding: WorkflowFormLookupBinding): string {
  return JSON.stringify([binding.version, binding.configurationCode.toLowerCase(), binding.domainCode.toLowerCase(), binding.resolutionMode ?? 'effective-context']);
}

const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const text = (value: unknown, maximum = 120): value is string => typeof value === 'string' && !!value.trim() && value.length <= maximum;
const keys = (value: Record<string, unknown>, allowed: readonly string[]) => Object.keys(value).every(key => allowed.includes(key));
const integer = (value: unknown, minimum: number) => typeof value === 'number' && Number.isSafeInteger(value) && value >= minimum;
export function workflowLookupSourceOwnerKey(owner: WorkflowLookupSourceOwner | null): string {
  return JSON.stringify(owner && [owner.draftId, owner.revision, owner.etag, owner.context.workflowCode, owner.context.subjectTypeCode,
    owner.context.scopeTypeCode, owner.context.scopeKey, owner.authorityKey]);
}
export function workflowLookupSourceReason(source: WorkflowLookupSource): string {
  if (source.disposition === 'Ambiguous') return 'More than one lookup source matches this workflow location.';
  if (source.disposition === 'Exempt') return 'This lookup source is not available in this workflow context.';
  if (source.disposition !== 'Resolved') return source.reasonCode === 'workflow.form.lookup.catalog_invalid'
    ? 'This source needs between 1 and 100 unique, supported choices with at least one enabled choice.'
    : 'This lookup source is unavailable in this workflow location.';
  return source.choices.some(choice => choice.isEnabled) ? '' : 'No enabled choices are available in this source.';
}
function validSource(value: unknown): value is WorkflowLookupSource {
  if (!record(value) || !keys(value, ['binding', 'label', 'description', 'disposition', 'reasonCode', 'choices', 'configurationVersionId', 'versionCode'])
    || !isWorkflowFormLookupBinding(value['binding']) || !text(value['label'], 300)
    || value['description'] != null && (typeof value['description'] !== 'string' || value['description'].length > 4000)
    || !['Resolved', 'Missing', 'Ambiguous', 'Exempt'].includes(String(value['disposition']))
    || value['reasonCode'] != null && !text(value['reasonCode'], 300)
    || !Array.isArray(value['choices']) || value['choices'].length > 100
    || value['configurationVersionId'] != null && !integer(value['configurationVersionId'], 1)
    || value['versionCode'] != null && !text(value['versionCode'])) return false;
  const codes = new Set<string>();
  return value['choices'].every(choice => {
    if (!record(choice) || !keys(choice, ['value', 'label', 'isEnabled']) || !text(choice['value']) || !text(choice['label'])
      || choice['value'] !== choice['value'].trim() || choice['label'] !== choice['label'].trim()
      || typeof choice['isEnabled'] !== 'boolean' || codes.has(choice['value'])) return false;
    codes.add(choice['value']); return true;
  }) && (value['disposition'] !== 'Resolved' || value['choices'].length > 0 && value['choices'].some(choice => choice.isEnabled));
}
function validReadOwner(value: Record<string, unknown>, owner: WorkflowLookupSourceOwner): boolean {
  const context = value['context'];
  return value['draftId'] === owner.draftId && value['revision'] === owner.revision && value['etag'] === owner.etag
    && typeof value['asOfUtc'] === 'string' && Number.isFinite(Date.parse(value['asOfUtc']))
    && record(context) && keys(context, ['workflowCode', 'subjectTypeCode', 'scopeTypeCode', 'scopeKey'])
    && context['workflowCode'] === owner.context.workflowCode && context['subjectTypeCode'] === owner.context.subjectTypeCode
    && context['scopeTypeCode'] === owner.context.scopeTypeCode && context['scopeKey'] === owner.context.scopeKey;
}
export function isWorkflowLookupSourcePage(value: unknown, owner: WorkflowLookupSourceOwner, query: WorkflowLookupSourceQuery): value is WorkflowLookupSourcePage {
  if (!record(value) || !keys(value, ['draftId', 'revision', 'etag', 'context', 'asOfUtc', 'pageNumber', 'pageSize', 'totalCount', 'items'])
    || !validReadOwner(value, owner) || value['pageNumber'] !== query.pageNumber || value['pageSize'] !== query.pageSize
    || !integer(value['totalCount'], 0) || !Array.isArray(value['items']) || value['items'].length > query.pageSize
    || value['items'].length > Number(value['totalCount']) || !value['items'].every(validSource)) return false;
  return new Set(value['items'].map(source => workflowLookupSourceBindingKey(source.binding))).size === value['items'].length;
}
export function isWorkflowLookupSourceRead(value: unknown, owner: WorkflowLookupSourceOwner, binding: WorkflowFormLookupBinding): value is WorkflowLookupSourceRead {
  return record(value) && keys(value, ['draftId', 'revision', 'etag', 'context', 'asOfUtc', 'source']) && validReadOwner(value, owner)
    && validSource(value['source']) && workflowLookupSourceBindingKey(value['source'].binding) === workflowLookupSourceBindingKey(binding);
}

/** Metadata is useful for source selection, never a runtime offer or permission to submit an answer. */
export function workflowLookupSourceMetadataReason(source: WorkflowLookupSource): string {
  const bounded = (value: unknown) => typeof value === 'string' && !!value.trim() && value === value.trim() && value.length <= 120;
  if (source?.disposition !== 'Resolved' || !isWorkflowFormLookupBinding(source.binding)
    || !Number.isSafeInteger(source.configurationVersionId) || (source.configurationVersionId ?? 0) <= 0
    || !Array.isArray(source.choices) || source.choices.length < 1 || source.choices.length > 100
    || source.choices.some(choice => !choice || !bounded(choice.value) || !bounded(choice.label) || typeof choice.isEnabled !== 'boolean')
    || new Set(source.choices.map(choice => choice.value)).size !== source.choices.length || !source.choices.some(choice => choice.isEnabled))
    return 'Choose an available lookup source with enabled choices for this workflow.';
  return '';
}
